import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import api from "PFCore/api";
import {
  ActivityCustomValue,
  Collection,
  CustomValue,
  CustomValueActivityConnection,
  Id,
  ProfileCustomValue,
  PureCustomValue,
  PureCustomValueCamelCased
} from "PFTypes";

export type CustomValuesQueryParams = {
  term?: string;
  ids?: Id[];
  customTypeId?: Id;
  approved?: boolean;
  curated?: boolean;
};

export const fetchCustomValues = ({
  term,
  customTypeId,
  ids,
  approved,
  curated
}: CustomValuesQueryParams): Promise<Collection<PureCustomValue[]>> =>
  api({
    url: "custom_values",
    params: {
      term,
      customTypeId,
      ...(ids ? { ids, perPage: PER_PAGE_UNPAGINATED } : {}),
      approved,
      curated
    }
  });

export const fetchProfileCustomValue = (customValueId: Id, profileId: Id) =>
  // TODO: [SP-2554] Response should be unified with fetchActivityCustomValue
  api<ProfileCustomValue>({
    url: `profiles/${profileId}/custom_values/${customValueId}`
  });

export const fetchActivityCustomValueConnection = (customValueId: Id, activityId: Id) =>
  api<CustomValueActivityConnection>({
    url: `activities/${activityId}/custom_values/${customValueId}/activity_connection`
  });

export const fetchCustomValue = (id: Id) =>
  api<PureCustomValueCamelCased>({
    url: `custom_values/${id}`
  });

export type FetchCustomValueParentChildValuesResponse = {
  parents: PureCustomValue[];
  children: PureCustomValue[];
};

export const fetchCustomValueParentChildValues = (id: Id) =>
  api<FetchCustomValueParentChildValuesResponse>({
    url: `custom_values/${id}/parents_and_children_skills`
  });

export type ProfilesAutocompleteCustomValuesQueryParams = {
  term: string;
  type: string;
  global?: boolean;
  includeStates?: string[];
};

export type CreateCustomValueResponse = Pick<
  CustomValue,
  "createdAt" | "customTypeId" | "globalId" | "id" | "state" | "updatedAt" | "value"
> & { mergedIntoId: number | null };

export type CreateCustomValueData = {
  payload: Pick<CustomValue, "customTypeId" | "value" | "globalId">;
  data?: {
    created?: boolean;
  } & Pick<CustomValue, "value" | "text" | "source" | "globalId">;
};

export const createCustomValue = (
  data: CreateCustomValueData["payload"]
): Promise<CreateCustomValueResponse> =>
  api({
    url: "custom_values",
    method: "POST",
    data
  });

export type CreateDictionaryConnectionParams = {
  customValueId: Id;
} & Partial<
  Pick<ActivityCustomValue, "value" | "source" | "globalId" | "requiredExperience" | "interest"> &
    Pick<ProfileCustomValue, "experience" | "top" | "developmental">
>;

export const createDictionaryConnection = (body: CreateDictionaryConnectionParams) =>
  api({
    url: "profile_connections",
    method: "POST",
    data: { interest: true, ...body }
  });

export const bulkCreateDictionaryConnections = (payloads: CreateDictionaryConnectionParams[]) =>
  Promise.allSettled(payloads.map((payload) => createDictionaryConnection(payload)));
